import { Button } from "@/components/ui/button";
import { whatsappService } from "@/services/whatsapp.service/whatsapp.api";
import { CheckCircle, Loader2, MessageCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

declare global {
  interface Window {
    FB: {
      init: (options: Record<string, unknown>) => void;
      login: (
        callback: (response: { authResponse?: { code: string } }) => void,
        options: Record<string, unknown>
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}

const GRAPH_API_VERSION = "v23.0";

interface SignupState {
  code?: string;
  wabaId?: string;
  phoneNumberId?: string;
  businessId?: string;
  event?: string;
}

interface EmbeddedSignupProps {
  onSuccess?: () => void;
}

export default function EmbeddedSignup({ onSuccess }: EmbeddedSignupProps) {
  const [signupState, setSignupState] = useState<SignupState>({});
  const [sdkReady, setSdkReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activated, setActivated] = useState(false);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  // Track when we have enough data to auto-activate
  const readyToActivate = useRef(false);

  useEffect(() => {
    // Init FB SDK
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: "426591926757554",
        autoLogAppEvents: true,
        xfbml: true,
        version: GRAPH_API_VERSION,
      });
      setSdkReady(true);
    };

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    document.body.appendChild(script);
    scriptRef.current = script;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return;

        setSignupState((prev) => ({
          ...prev,
          event: data.event,
          wabaId: data.data?.waba_id ?? prev.wabaId,
          phoneNumberId: data.data?.phone_number_id ?? prev.phoneNumberId,
          businessId: data.data?.business_id ?? prev.businessId,
        }));
      } catch {
        // Non-JSON messages from FB are silently ignored
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      if (scriptRef.current && document.body.contains(scriptRef.current)) {
        document.body.removeChild(scriptRef.current);
      }
    };
  }, []);

  // Auto-activate when we have code + wabaId + phoneNumberId
  useEffect(() => {
    if (
      signupState.code &&
      signupState.wabaId &&
      signupState.phoneNumberId &&
      !readyToActivate.current &&
      !activated
    ) {
      readyToActivate.current = true;
      void doActivate(signupState);
    }
  }, [signupState, activated]);

  async function doActivate(state: SignupState) {
    if (!state.code || !state.wabaId || !state.phoneNumberId) return;
    setLoading(true);
    const [res, err] = await whatsappService.activate({
      code: state.code,
      wabaId: state.wabaId,
      phoneNumberId: state.phoneNumberId,
      businessId: state.businessId,
    });
    setLoading(false);

    if (err || !res?.success) {
      toast.error("Erro ao ativar WhatsApp. Tente novamente.");
      readyToActivate.current = false;
      return;
    }

    setActivated(true);
    toast.success("WhatsApp ativado com sucesso!");
    onSuccess?.();
  }

  function launchWhatsAppSignup() {
    if (!window.FB) {
      toast.error("SDK do Facebook não carregado. Tente recarregar a página.");
      return;
    }

    readyToActivate.current = false;
    setSignupState({});
    setActivated(false);

    window.FB.login(
      (response) => {
        if (response.authResponse?.code) {
          setSignupState((prev) => ({
            ...prev,
            code: response.authResponse!.code,
          }));
        } else {
          toast.error("Login cancelado ou sem autorização.");
        }
      },
      {
        config_id: "859868522620455",
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "",
          sessionInfoVersion: "3",
        },
      }
    );
  }

  if (activated) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <CheckCircle className="h-12 w-12 text-green-500" />
        <p className="text-lg font-semibold">WhatsApp conectado com sucesso!</p>
        <p className="text-sm text-muted-foreground">
          Sua conta WhatsApp Business foi integrada. Você já pode receber e enviar mensagens.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <MessageCircle className="h-10 w-10 text-green-500" />
        <h3 className="text-lg font-semibold">Conectar via Facebook (Embedded Signup)</h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          Clique no botão abaixo para autorizar o acesso ao seu WhatsApp Business Account. Você
          será redirecionado para o Facebook.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Ativando sua conta WhatsApp...</span>
        </div>
      ) : (
        <button
          type="button"
          onClick={launchWhatsAppSignup}
          disabled={!sdkReady}
          className="flex items-center justify-center gap-2 rounded-md bg-[#1877f2] px-6 py-3 font-bold text-white transition-colors hover:bg-[#166fe5] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sdkReady ? "Login com Facebook" : "Carregando SDK..."}
        </button>
      )}

      {signupState.event === "CANCEL" && (
        <p className="text-sm text-amber-600">Fluxo cancelado. Tente novamente quando quiser.</p>
      )}
    </div>
  );
}
